<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          Add a Chat
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_group"
                inset
                :label="`Chat Type: ${form.is_group ? 'Group Chat' : 'Direct Chat'}`"
              ></v-switch>
            </v-col>
            <v-col sm="6">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Status: ${form.is_active ? 'Active' : 'Inactive'}`"
              ></v-switch>
            </v-col>
            <v-col sm="12" v-if="form.is_group">
              <v-text-field
                v-model="form.name"
                solo
                rounded
                label="Chat Name *"
                prepend-inner-icon="mdi-message-text"
                :error-messages="errors.name"
                placeholder="Enter group chat name..."
              ></v-text-field>
            </v-col>
            <v-col sm="12" v-if="form.is_group">
              <v-text-field
                v-model="form.image_path"
                solo
                rounded
                label="Image Path"
                prepend-inner-icon="mdi-image"
                :error-messages="errors.image_path"
                placeholder="Enter image URL or path..."
              ></v-text-field>
            </v-col>
            
            <!-- User Selection for Group Chat -->
            <v-col sm="12" v-if="form.is_group">
              <v-autocomplete
                v-model="form.user_ids"
                :items="users"
                item-text="user_name"
                item-value="id"
                label="Select Users *"
                multiple
                chips
                deletable-chips
                solo
                rounded
                prepend-inner-icon="mdi-account-group"
                :error-messages="errors.user_ids"
                placeholder="Choose users to add to the group..."
              >
                <template v-slot:item="{ item }">
                  <v-list-item-avatar>
                    <v-img v-if="item.image_path" :src="`${mediaUrl}${item.image_path}`"></v-img>
                    <v-icon v-else>mdi-account-circle</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.user_name || item.email }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.first_name }} {{ item.last_name }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{ item }">
                  <v-chip close @click:close="removeUser(item.id)">
                    <v-avatar left>
                      <v-img v-if="item.image_path" :src="`${mediaUrl}${item.image_path}`"></v-img>
                      <v-icon v-else>mdi-account-circle</v-icon>
                    </v-avatar>
                    {{ item.user_name || item.email }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- User Selection for Direct Chat -->
            <v-col sm="12" v-if="!form.is_group">
              <v-autocomplete
                v-model="form.user_id"
                :items="users"
                item-text="user_name"
                item-value="id"
                label="Select User *"
                solo
                rounded
                prepend-inner-icon="mdi-account"
                :error-messages="errors.user_id"
                placeholder="Choose user for direct chat..."
              >
                <template v-slot:item="{ item }">
                  <v-list-item-avatar>
                    <v-img v-if="item.image_path" :src="`${mediaUrl}${item.image_path}`"></v-img>
                    <v-icon v-else>mdi-account-circle</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.user_name || item.email }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.first_name }} {{ item.last_name }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Chat Preview Card -->
          <v-card outlined class="mt-4" v-if="form.is_group || form.name">
            <v-card-title>
              <v-icon left>mdi-eye</v-icon>
              Chat Preview
            </v-card-title>
            <v-card-text>
              <v-row align="center">
                <v-col cols="auto">
                  <v-avatar size="60" :color="form.image_path ? 'transparent' : 'grey lighten-2'">
                    <v-img v-if="form.image_path" :src="form.image_path" alt="Chat Image"></v-img>
                    <v-icon v-else size="30" color="grey">
                      {{ form.is_group ? 'mdi-account-group' : 'mdi-account' }}
                    </v-icon>
                  </v-avatar>
                </v-col>
                <v-col>
                  <div class="subtitle-1 font-weight-medium">
                    {{ form.name || (form.is_group ? 'Group Chat' : 'Direct Chat') }}
                  </div>
                  <div class="caption grey--text">
                    {{ form.is_group ? 'Group Chat' : 'Direct Chat' }} • 
                    {{ form.is_active ? 'Active' : 'Inactive' }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-divider class="mt-5 mb-5"></v-divider>
          <v-row>
            <v-col sm="12">
              <v-btn
                :loading="isSaving"
                :disabled="isSaving"
                color="primary"
                @click="saveData"
                class="mr-4"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save Chat
              </v-btn>
              <v-btn
                color="secondary"
                @click="$router.back()"
                :disabled="isSaving"
              >
                <v-icon left>mdi-cancel</v-icon>
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Chat created successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="
                          showSuccessDialog = false;
                          $router.push('/chats');
                        "
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ChatCreatePage",
  data() {
    return {
      form: {
        is_group: false,
        name: "",
        image_path: "",
        is_active: true,
        user_ids: [],
        user_id: null,
      },
      users: [],
      errors: {},
      isSaving: false,
      showSuccessDialog: false,
    };
  },
  computed: {
    user() {
      return this.$store.getters["auth/user"];
    },
    mediaUrl() {
      return this.$store.getters["settings/getMediaUrl"];
    },
  },
  mounted() {
    this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      try {
        const response = await axios.get("/users");
        this.users = response.data.data || [];
        // Filter out current user from the list
        this.users = this.users.filter(user => user.id !== this.user.id);
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$store.dispatch("errors/setError", {
          message: "Failed to fetch users list.",
        });
      }
    },

    removeUser(userId) {
      this.form.user_ids = this.form.user_ids.filter(id => id !== userId);
    },

    async saveData() {
      this.isSaving = true;
      this.errors = {};

      try {
        const chatData = {
          is_group: this.form.is_group,
          name: this.form.name || null,
          image_path: this.form.image_path || null,
          is_active: this.form.is_active,
        };

        // Add user selection based on chat type
        if (this.form.is_group) {
          chatData.user_ids = this.form.user_ids;
        } else {
          chatData.user_id = this.form.user_id;
        }

        const response = await axios.post("/chats", chatData);

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.resetForm();
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while creating the chat.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            // Authentication error
            this.$store.dispatch("errors/setError", {
              message: "Authentication required. Please login again.",
            });
            this.$router.push("/login");
          } else {
            // Other errors
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while creating the chat.",
            });
          }
        } else {
          // Network or other errors
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    resetForm() {
      this.form = {
        is_group: false,
        name: "",
        image_path: "",
        is_active: true,
        user_ids: [],
        user_id: null,
      };
      this.errors = {};
    },
  },
};
</script>

<style scoped>
.v-card-title {
  font-weight: bold;
  font-size: 1.5rem;
}

.v-text-field.outlined .v-input__control {
  min-height: 48px;
}
</style>